import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  StyleSheet,
  Text,
  View,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Dimensions,
  Modal,
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Data from './Data';
import { getStorage } from '../storage';
import { useScale, ScaleFn, POSTURE_OPTS, postureText } from './common';

// ─── 테마 (Diaper.tsx 와 동일 톤) ────────
const C = {
  bg:        '#F9FAFB',
  card:      '#FFFFFF',
  cardInner: '#F3F4F6',
  border:    '#E5E7EB',
  text:      '#111827',
  textSub:   '#4B5563',
  muted:     '#9CA3AF',
  accent:    '#38BDF8',
  ok:        '#34D399',
  warn:      '#FB923C',
  err:       '#F87171',
};

type Person = { IDX: number; NAME: string; AREA: string; ROOM_NO: string };
type Rec = { IDX: number; PERSON_IDX: number; PERSON_NAME: string; POSTURE: string; RECORDER: string; REGDATE: string };

const pad2 = (n: number) => String(n).padStart(2, '0');
const fmtDate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
const fmtDT = (d: Date) => `${fmtDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
// 현재 시각을 5분 단위(내림)로 — 시간 선택(5분 간격)과 값 일치용
const nowStr = () => { const d = new Date(); d.setMinutes(Math.floor(d.getMinutes() / 5) * 5, 0, 0); return fmtDT(d); };

// react-datepicker 커스텀 인풋 — macOS 에서 datetime-local 은 24시간제 강제가 안 돼서 이걸 쓴다
const DTInput = React.forwardRef(function DTInput(props: any, ref: any) {
  return (
    <input
      ref={ref}
      onClick={props.onClick}
      value={props.value}
      readOnly
      placeholder="YYYY-MM-DD HH:mm"
      style={{ width: '100%', padding: 10, fontSize: 15, border: '1px solid ' + C.border, borderRadius: 8, backgroundColor: C.card, color: C.text, cursor: 'pointer', boxSizing: 'border-box' }}
    />
  );
});

function useCardWidth() {
  const [w, setW] = useState(Dimensions.get('window').width);
  useEffect(() => {
    const sub = Dimensions.addEventListener('change', ({ window }) => setW(window.width));
    return () => sub.remove();
  }, []);
  const cols = w < 480 ? 2 : w < 720 ? 3 : w < 1024 ? 4 : 5;
  const gap = 10;
  const padding = 24;
  return Math.floor((w - padding - gap * (cols - 1)) / cols);
}

export default function Posture() {
  const isFocused = useIsFocused();
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);
  const cardW = useCardWidth();
  const scrollRef = useRef<ScrollView>(null);

  const [persons, setPersons] = useState<Person[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedArea, setSelectedArea] = useState<string>('');
  const [selectedPerson, setSelectedPerson] = useState<Person | null>(null);

  const [posture, setPosture] = useState<string>('');
  const [regDate, setRegDate] = useState<string>(nowStr());
  const [toast, setToast] = useState<string>('');
  const [toastErr, setToastErr] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [recent, setRecent] = useState<Rec[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);

  // 수정·삭제는 최고관리자(ADMIN='0')만. DiaperViewer 와 같은 방식으로 읽는다.
  const [admin, setAdmin] = useState('1');
  useEffect(() => { getStorage('AutoLogin').then((u: any) => setAdmin(u?.admin ?? '1')); }, []);
  // [2026-10-08] 기록자를 손으로 적던 입력란을 없애고 로그인 ID 를 자동으로 쓴다.
  // 세션에는 한글 이름이 없고 로그인 ID(id)만 있다(Login.tsx sessionData).
  const [loginId, setLoginId] = useState<string>('');
  useEffect(() => { getStorage('Session').then((v: any) => setLoginId(v?.id || '')); }, []);
  const isAdmin = String(admin) === '0';

  // 수정 모달
  const [editing, setEditing] = useState<Rec | null>(null);
  const [edPosture, setEdPosture] = useState('');
  const [edRecorder, setEdRecorder] = useState('');
  const [edRegDate, setEdRegDate] = useState('');
  const [edSaving, setEdSaving] = useState(false);

  // 이용자·기록자 목록은 기저귀 쪽 API 를 그대로 쓴다(배정·구역 로직 중복 금지)
  useEffect(() => {
    if (!isFocused) return;
    setLoading(true);
    Data.fetchData('diaper/personList', {}, (result: Person[]) => {
      setPersons(result || []);
      setLoading(false);
    }, () => setLoading(false));
    loadRecent();
  }, [isFocused]);

  const loadRecent = () => {
    setRecentLoading(true);
    Data.fetchData('posture/list', {}, (result: Rec[]) => {
      setRecent(result || []);
      setRecentLoading(false);
    }, () => setRecentLoading(false));
  };

  const areas = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    persons.forEach(p => { if (!seen.has(p.AREA)) { seen.add(p.AREA); out.push(p.AREA); }});
    return out;
  }, [persons]);

  useEffect(() => {
    if (areas.length && !selectedArea) setSelectedArea(areas[0]);
  }, [areas]);

  const personsInArea = useMemo(
    () => persons.filter(p => p.AREA === selectedArea),
    [persons, selectedArea]
  );

  const showToast = (msg: string, err: boolean = false) => {
    setToast(msg);
    setToastErr(err);
    setTimeout(() => setToast(''), 2800);
  };

  const resetForm = () => {
    setPosture(''); setRegDate(nowStr());
  };

  const submit = () => {
    if (submitting) return;
    if (!selectedPerson)  { showToast('이용자를 선택해 주세요', true); return; }
    if (!posture)         { showToast('자세를 선택해 주세요', true); return; }
    if (!loginId) { showToast('로그인 정보를 확인할 수 없습니다. 다시 로그인해 주세요', true); return; }
    // 기록 시각 — 형식 + 미래 거부
    const rd = regDate.trim();
    if (!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(rd)) { showToast('기록 시각 형식: YYYY-MM-DD HH:mm', true); return; }
    const rdt = new Date(rd.replace(' ', 'T'));
    if (isNaN(rdt.getTime())) { showToast('기록 시각이 올바르지 않습니다', true); return; }
    if (rdt.getTime() > Date.now() + 120000) { showToast('미래 시각은 입력할 수 없습니다', true); return; }

    setSubmitting(true);
    Data.fetchData('posture/insert', {
      PERSON_IDX: selectedPerson.IDX,
      POSTURE: posture,
      RECORDER: loginId,        // 손입력 제거 — 로그인 ID 를 그대로 남긴다
      REGDATE: rd + ':00',
    }, (result: any) => {
      setSubmitting(false);
      if (result && result.IDX) {
        const label = POSTURE_OPTS.find(o => o.v === posture)?.label || posture;
        showToast(`✓ ${selectedPerson.NAME} ${label} 기록 완료`);
        resetForm();
        setSelectedPerson(null);
        loadRecent();
      } else {
        showToast('등록 실패 — 서버 응답 오류', true);
      }
    }, () => { setSubmitting(false); showToast('등록 실패 — 네트워크 오류', true); });
  };

  const openEdit = (r: Rec) => {
    setEditing(r);
    setEdPosture(r.POSTURE || '');
    setEdRecorder(r.RECORDER || '');
    setEdRegDate((r.REGDATE || '').replace('T', ' ').slice(0, 16));
  };
  const closeEdit = () => { setEditing(null); setEdSaving(false); };

  const saveEdit = () => {
    if (!editing || edSaving) return;
    if (!edPosture) { showToast('자세를 선택해 주세요', true); return; }
    const rd = edRegDate.trim();
    if (!/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(rd)) { showToast('기록 시각 형식: YYYY-MM-DD HH:mm', true); return; }
    const rdt = new Date(rd.replace(' ', 'T'));
    if (isNaN(rdt.getTime())) { showToast('기록 시각이 올바르지 않습니다', true); return; }
    if (rdt.getTime() > Date.now() + 120000) { showToast('미래 시각은 입력할 수 없습니다', true); return; }

    setEdSaving(true);
    Data.fetchData('posture/update', {
      IDX: editing.IDX,
      POSTURE: edPosture,
      REGDATE: rd + ':00',
    }, (result: any) => {
      setEdSaving(false);
      if (result && result.IDX) { closeEdit(); showToast('수정했습니다'); loadRecent(); }
      else showToast('수정 실패 — 서버 응답 오류', true);
    }, () => { setEdSaving(false); showToast('수정 실패 — 네트워크 오류', true); });
  };

  const removeRec = (r: Rec) => {
    if (typeof window !== 'undefined' &&
        !window.confirm(`${r.PERSON_NAME} · ${postureText(r.POSTURE)} · ${(r.REGDATE || '').slice(0, 16)}\n이 기록을 삭제할까요?`)) return;
    Data.fetchData('posture/delete', { IDX: r.IDX }, (result: any) => {
      if (result && result.IDX) { showToast('삭제했습니다'); loadRecent(); }
      else showToast('삭제 실패', true);
    }, () => showToast('삭제 실패 — 네트워크 오류', true));
  };

  // 이용자 선택 시 폼이 보이도록 스크롤
  useEffect(() => {
    if (selectedPerson && scrollRef.current) {
      setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 100);
    }
  }, [selectedPerson]);

  return (
    <KeyboardAvoidingView
      style={[styles.root, { backgroundColor: C.bg }]}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 80 : 0}
    >
      {/* 구역 탭 */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.areaTabs} contentContainerStyle={{ paddingHorizontal: s(12), alignItems: 'center' }}>
        {areas.map(a => (
          <TouchableOpacity
            key={a}
            onPress={() => { setSelectedArea(a); setSelectedPerson(null); resetForm(); }}
            style={[styles.areaTab, selectedArea === a && styles.areaTabActive]}
          >
            <Text style={[styles.areaTabText, selectedArea === a && styles.areaTabTextActive]}>{a}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <ScrollView
        ref={scrollRef}
        style={{ flex: 1 }}
        contentContainerStyle={{ padding: s(12), paddingBottom: s(60) }}
        keyboardShouldPersistTaps="handled"
      >
        {loading && <ActivityIndicator color={C.accent} style={{ marginTop: s(20) }} />}

        {/* 이용자 카드 그리드 */}
        <View style={styles.grid}>
          {personsInArea.map(p => {
            const active = selectedPerson?.IDX === p.IDX;
            return (
              <TouchableOpacity
                key={p.IDX}
                onPress={() => { setSelectedPerson(p); resetForm(); }}
                style={[styles.card, { width: cardW }, active && styles.cardActive]}
                activeOpacity={0.7}
              >
                <Text style={[styles.cardName, active && { color: '#FFF' }]} numberOfLines={1}>{p.NAME}</Text>
                <Text style={[styles.cardRoom, active && { color: '#E0F2FE' }]} numberOfLines={1}>{p.ROOM_NO || '-'}</Text>
              </TouchableOpacity>
            );
          })}
        </View>

        {/* 입력 폼 — 이용자 선택 시 노출. 받는 건 시각·자세·기록자 셋뿐이다. */}
        {selectedPerson && (
          <View style={styles.formCard}>
            <View style={styles.formHeader}>
              <Text style={styles.formHeaderText} numberOfLines={1}>
                {selectedPerson.NAME}  <Text style={styles.formHeaderSub}>{selectedPerson.ROOM_NO}</Text>
              </Text>
              <TouchableOpacity
                onPress={() => { setSelectedPerson(null); resetForm(); }}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                style={{ marginLeft: s(12) }}
              >
                <Ionicons name="close" size={s(26)} color={C.textSub} />
              </TouchableOpacity>
            </View>

            {/* 기록 시각 */}
            <Text style={styles.label}>기록 시각 (날짜·시간 선택, 24시간)</Text>
            <View style={{ flexDirection: 'row', gap: s(8), alignItems: 'center' }}>
              {Platform.OS === 'web' ? (
                <View style={{ flex: 1 }}>
                  {/* 팝업이 폼(ScrollView) 안에서 잘리지 않게 portal + 높은 z-index */}
                  <style>{`.react-datepicker-popper{z-index:99999!important}`}</style>
                  <DatePicker
                    selected={regDate ? new Date(regDate.replace(' ', 'T')) : new Date()}
                    onChange={(date: any) => { if (date) setRegDate(fmtDT(date)); }}
                    dateFormat="yyyy-MM-dd HH:mm"
                    timeFormat="HH:mm"
                    showTimeSelect
                    timeIntervals={5}
                    maxDate={new Date()}
                    portalId="posture-datepicker-portal"
                    popperProps={{ strategy: 'fixed' }}
                    customInput={<DTInput />}
                  />
                </View>
              ) : (
                <TextInput
                  value={regDate}
                  onChangeText={setRegDate}
                  placeholder="YYYY-MM-DD HH:mm"
                  placeholderTextColor={C.muted}
                  style={[styles.input, { flex: 1 }]}
                />
              )}
              <TouchableOpacity onPress={() => setRegDate(nowStr())} style={{ paddingHorizontal: s(12), paddingVertical: s(10), backgroundColor: C.cardInner, borderRadius: s(8), borderWidth: 1, borderColor: C.border }}>
                <Text style={{ color: C.textSub, fontWeight: '600', fontSize: s(13) }}>현재</Text>
              </TouchableOpacity>
            </View>

            {/* 자세 — 5종. 값은 센서 판정과 같은 코드로 저장된다. */}
            <Text style={styles.label}>자세</Text>
            <View style={styles.postureGrid}>
              {POSTURE_OPTS.map(op => (
                <TouchableOpacity
                  key={op.v}
                  onPress={() => setPosture(posture === op.v ? '' : op.v)}
                  style={[styles.postureChip, posture === op.v && styles.chipActive]}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.chipBigText, posture === op.v && styles.chipTextActive]}>{op.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* 기록자 입력란 제거(2026-10-08) — 로그인 ID 가 자동으로 기록된다 */}
            <Text style={[styles.label, { marginBottom: s(2) }]}>기록자</Text>
            <Text style={{ fontSize: s(13), color: C.textSub, marginBottom: s(14) }}>
              {loginId ? loginId + ' (로그인 계정으로 자동 기록)' : '로그인 정보 확인 중...'}
            </Text>

            <TouchableOpacity
              onPress={submit}
              disabled={submitting}
              style={[styles.submit, submitting && { opacity: 0.6 }]}
              activeOpacity={0.8}
            >
              <Text style={styles.submitText}>{submitting ? '등록 중...' : '자세 기록'}</Text>
            </TouchableOpacity>
          </View>
        )}

        {/* 최근 기록 */}
        <View style={[styles.formCard, { marginTop: s(16) }]}>
          <View style={styles.formHeader}>
            <Text style={styles.formHeaderText}>
              최근 자세 기록 <Text style={styles.formHeaderSub}>{recent.length}건</Text>
            </Text>
            <TouchableOpacity onPress={loadRecent} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
              <Ionicons name="refresh" size={s(20)} color={C.textSub} />
            </TouchableOpacity>
          </View>

          {recentLoading && <ActivityIndicator color={C.accent} style={{ marginVertical: s(14) }} />}

          <View style={styles.tableHeader}>
            <Text style={[styles.cell, styles.headerText, { flex: 1.6 }]}>이름</Text>
            <Text style={[styles.cell, styles.headerText, { flex: 1.2 }]}>자세</Text>
            <Text style={[styles.cell, styles.headerText, { flex: 1.3 }]}>기록자</Text>
            <Text style={[styles.cell, styles.headerText, { flex: 2 }]}>일시</Text>
            <Text style={[styles.cell, styles.headerText, { flex: 1.1 }]}>관리</Text>
          </View>

          {!recentLoading && recent.length === 0 && (
            <Text style={{ textAlign: 'center', color: C.textSub, padding: s(24) }}>기록 없음</Text>
          )}

          {recent.slice(0, 50).map((r, i) => (
            <View key={r.IDX} style={[styles.tableRow, i % 2 === 1 && { backgroundColor: C.cardInner }]}>
              <Text style={[styles.cell, { flex: 1.6 }]} numberOfLines={1}>{r.PERSON_NAME || '-'}</Text>
              <Text style={[styles.cell, { flex: 1.2, color: C.accent, fontWeight: '700' }]}>{postureText(r.POSTURE)}</Text>
              <Text style={[styles.cell, { flex: 1.3 }]} numberOfLines={1}>{r.RECORDER}</Text>
              <Text style={[styles.cell, { flex: 2, fontSize: s(12) }]}>{(r.REGDATE || '').replace('T', ' ').slice(0, 16)}</Text>
              <View style={{ flex: 1.1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: s(10) }}>
                {/* 수정 — 본인 실수를 바로 고칠 수 있어야 해서 일반 사용자도 쓴다 */}
                <TouchableOpacity onPress={() => openEdit(r)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <Ionicons name="create-outline" size={s(18)} color={C.accent} />
                </TouchableOpacity>
                {/* 삭제 — 기록을 없애는 일이라 최고관리자만 */}
                {isAdmin && (
                  <TouchableOpacity onPress={() => removeRec(r)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                    <Ionicons name="trash-outline" size={s(17)} color={C.err} />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          ))}
          {recent.length > 50 && (
            <Text style={{ textAlign: 'center', color: C.muted, paddingTop: s(10), fontSize: s(12) }}>
              최근 50건만 표시 (전체 {recent.length}건)
            </Text>
          )}
        </View>
      </ScrollView>

      {/* ─── 수정 모달 ─── */}
      <Modal visible={!!editing} transparent animationType="fade" onRequestClose={closeEdit}>
        <View style={styles.modalBg}>
          <View style={styles.modalCard}>
            <View style={styles.formHeader}>
              <Text style={styles.modalTitle} numberOfLines={1}>
                자세 기록 수정 <Text style={styles.formHeaderSub}>{editing?.PERSON_NAME}</Text>
              </Text>
              <TouchableOpacity onPress={closeEdit} hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}>
                <Ionicons name="close" size={s(24)} color={C.textSub} />
              </TouchableOpacity>
            </View>

            <Text style={styles.label}>기록 시각</Text>
            {Platform.OS === 'web' ? (
              <View>
                <style>{`.react-datepicker-popper{z-index:99999!important}`}</style>
                <DatePicker
                  selected={edRegDate ? new Date(edRegDate.replace(' ', 'T')) : new Date()}
                  onChange={(date: any) => { if (date) setEdRegDate(fmtDT(date)); }}
                  dateFormat="yyyy-MM-dd HH:mm"
                  timeFormat="HH:mm"
                  showTimeSelect
                  timeIntervals={5}
                  maxDate={new Date()}
                  portalId="posture-edit-datepicker-portal"
                  popperProps={{ strategy: 'fixed' }}
                  customInput={<DTInput />}
                />
              </View>
            ) : (
              <TextInput value={edRegDate} onChangeText={setEdRegDate} style={styles.modalInput} />
            )}

            <Text style={styles.label}>자세</Text>
            <View style={styles.postureGrid}>
              {POSTURE_OPTS.map(op => (
                <TouchableOpacity
                  key={op.v}
                  onPress={() => setEdPosture(op.v)}
                  style={[styles.postureChipSm, edPosture === op.v && styles.chipActive]}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.chipSmText, edPosture === op.v && styles.chipTextActive]}>{op.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            {/* 기록자 입력란 제거(2026-10-08) — 수정해도 원래 기록자를 그대로 둔다 */}
            <Text style={[styles.label, { marginBottom: s(2) }]}>기록자</Text>
            <Text style={{ fontSize: s(13), color: C.textSub, marginBottom: s(4) }}>
              {edRecorder || '-'}
            </Text>

            <View style={{ flexDirection: 'row', gap: s(10), marginTop: s(20) }}>
              <TouchableOpacity onPress={closeEdit} style={[styles.modalBtn, { backgroundColor: C.cardInner, borderWidth: 1, borderColor: C.border }]}>
                <Text style={{ color: C.textSub, fontWeight: '700', fontSize: s(15) }}>취소</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={saveEdit} disabled={edSaving} style={[styles.modalBtn, { backgroundColor: C.accent }, edSaving && { opacity: 0.6 }]}>
                <Text style={{ color: '#FFF', fontWeight: '700', fontSize: s(15) }}>{edSaving ? '저장 중...' : '저장'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {toast ? (
        <View style={[styles.toast, toastErr && { backgroundColor: C.err }]}>
          <Text style={styles.toastText}>{toast}</Text>
        </View>
      ) : null}
    </KeyboardAvoidingView>
  );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
  root: { flex: 1, overflow: 'hidden' },
  areaTabs: { flexGrow: 0, paddingVertical: s(6), borderBottomWidth: 1, borderBottomColor: C.border, backgroundColor: C.card, maxHeight: s(56) },
  areaTab: { paddingHorizontal: s(18), paddingVertical: s(10), borderRadius: s(22), backgroundColor: C.cardInner, marginRight: s(8) },
  areaTabActive: { backgroundColor: C.accent },
  areaTabText: { color: C.textSub, fontWeight: '600', fontSize: s(15) },
  areaTabTextActive: { color: '#FFF' },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: s(10), marginBottom: s(14) },
  card: { padding: s(14), borderRadius: s(12), backgroundColor: C.card, borderWidth: 1, borderColor: C.border, alignItems: 'center', minHeight: s(70), justifyContent: 'center' },
  cardActive: { backgroundColor: C.accent, borderColor: C.accent },
  cardName: { fontSize: s(17), fontWeight: '700', color: C.text },
  cardRoom: { fontSize: s(12), color: C.textSub, marginTop: s(4) },
  formCard: { backgroundColor: C.card, borderRadius: s(14), padding: s(18), marginTop: s(8), borderWidth: 1, borderColor: C.border, shadowColor: '#000', shadowOpacity: 0.06, shadowRadius: s(8), shadowOffset: { width: 0, height: 2 }, elevation: 2 },
  formHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: s(14), paddingBottom: s(12), borderBottomWidth: 1, borderBottomColor: C.border },
  formHeaderText: { fontSize: s(19), fontWeight: '700', color: C.text, flex: 1 },
  formHeaderSub: { fontSize: s(14), color: C.textSub, fontWeight: '400' },
  label: { fontSize: s(14), color: C.textSub, marginTop: s(14), marginBottom: s(8), fontWeight: '600' },
  // 자세 5개는 한 줄에 다 넣으면 글자가 좁아진다 → 줄바꿈 허용 + 최소폭
  postureGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: s(10) },
  postureChip: { flexGrow: 1, minWidth: s(96), paddingVertical: s(16), borderRadius: s(10), borderWidth: 1, borderColor: C.border, backgroundColor: C.cardInner, alignItems: 'center', minHeight: s(50), justifyContent: 'center' },
  chipBigText: { fontSize: s(16), color: C.textSub, fontWeight: '700' },
  chipActive: { backgroundColor: C.accent, borderColor: C.accent },
  chipTextActive: { color: '#FFF' },
  input: { borderWidth: 1, borderColor: C.border, borderRadius: s(10), paddingHorizontal: s(14), paddingVertical: s(14), fontSize: s(17), color: C.text, backgroundColor: C.cardInner, minHeight: s(50) },
  submit: { marginTop: s(22), backgroundColor: C.accent, paddingVertical: s(18), borderRadius: s(12), alignItems: 'center', minHeight: s(56) },
  submitText: { color: '#FFF', fontSize: s(18), fontWeight: '700', letterSpacing: 1 },
  tableHeader: { flexDirection: 'row', paddingVertical: s(10), borderBottomWidth: 1, borderBottomColor: C.border, backgroundColor: C.cardInner, borderRadius: s(6) },
  tableRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: s(11), borderBottomWidth: 1, borderBottomColor: C.border },
  cell: { fontSize: s(13), color: C.text, textAlign: 'center', paddingHorizontal: s(4) },
  headerText: { color: C.textSub, fontWeight: '700', fontSize: s(12) },
  modalBg: { flex: 1, backgroundColor: 'rgba(15,23,42,0.55)', justifyContent: 'center', alignItems: 'center', padding: s(16) },
  modalCard: { width: '100%', maxWidth: s(480), backgroundColor: C.card, borderRadius: s(14), padding: s(18), shadowColor: '#000', shadowOpacity: 0.3, shadowRadius: s(16), shadowOffset: { width: 0, height: 8 }, elevation: 6 },
  modalTitle: { fontSize: s(19), fontWeight: '700', color: C.text, flex: 1 },
  modalInput: { borderWidth: 1, borderColor: C.border, borderRadius: s(8), paddingHorizontal: s(12), paddingVertical: s(10), fontSize: s(15), color: C.text, backgroundColor: C.cardInner },
  // 모달 안은 좁으니 칩을 작게
  postureChipSm: { flexGrow: 1, minWidth: s(80), paddingVertical: s(11), borderRadius: s(8), borderWidth: 1, borderColor: C.border, backgroundColor: C.cardInner, alignItems: 'center' },
  chipSmText: { fontSize: s(14), color: C.textSub, fontWeight: '700' },
  modalBtn: { flex: 1, paddingVertical: s(14), borderRadius: s(10), alignItems: 'center' },
  toast: { position: 'absolute', bottom: s(30), left: s(16), right: s(16), backgroundColor: C.ok, padding: s(14), borderRadius: s(12), alignItems: 'center', shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: s(8), shadowOffset: { width: 0, height: 4 }, elevation: 4 },
  toastText: { color: '#FFF', fontSize: s(15), fontWeight: '700' },
});
